import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import { toastError, toastSuccess } from "@/lib/toast";
import {
  CIRCATRON_SCOPE_PRESETS,
  matchScopePreset,
  scopesForPreset,
} from "@/lib/circatronScopePresets";

const EVENT_OPTIONS = [
  { id: "member.joined", label: "Member joined" },
  { id: "post.created", label: "Post created" },
  { id: "post.critical", label: "Post marked critical" },
];

const SCOPE_OPTIONS = [
  { id: "channel.post", label: "Post to bound channel", needsTeams: false },
  { id: "posts.read.team", label: "Read team posts", needsTeams: true },
  { id: "posts.read.org", label: "Read org feed", needsTeams: false },
  { id: "posts.write.team", label: "Create team posts", needsTeams: true },
  { id: "posts.write.org", label: "Create org-wide posts", needsTeams: false },
  { id: "kanban.update", label: "Create/update kanban cards", needsTeams: true },
];

function scopesNeedTeams(scopes) {
  return SCOPE_OPTIONS.some((s) => s.needsTeams && scopes.includes(s.id));
}

function initialScopesForPack(pack) {
  const fromPreset = scopesForPreset(pack?.default_scope_preset);
  if (fromPreset?.length) return fromPreset;
  return ["channel.post"];
}

/**
 * Guided install for a Circatron pack (Slice C + D hard_inbound).
 */
const CircatronPackInstallWizard = ({
  orgId,
  accessToken,
  pack,
  channels,
  teams,
  onCancel,
  onInstalled,
}) => {
  const fields = pack?.wizard_fields || [];
  const labels = pack?.field_labels || {};
  const placeholders = pack?.field_placeholders || {};

  const [name, setName] = useState(pack?.default_name || "");
  const [channelId, setChannelId] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [events, setEvents] = useState(
    Array.isArray(pack?.default_events) && pack.default_events.length
      ? [...pack.default_events]
      : ["post.critical"]
  );
  const [scopes, setScopes] = useState(() => initialScopesForPack(pack));
  const [scopePreset, setScopePreset] = useState(
    () => pack?.default_scope_preset || "channel_bot"
  );
  const [teamIds, setTeamIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setName(pack?.default_name || "");
    setChannelId("");
    setAvatarUrl("");
    setTargetUrl("");
    setEvents(
      Array.isArray(pack?.default_events) && pack.default_events.length
        ? [...pack.default_events]
        : ["post.critical"]
    );
    const nextScopes = initialScopesForPack(pack);
    setScopes(nextScopes);
    setScopePreset(pack?.default_scope_preset || matchScopePreset(nextScopes));
    setTeamIds([]);
  }, [pack]);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const toggleEvent = (id) => {
    setEvents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applyPreset = (presetId) => {
    setScopePreset(presetId);
    if (presetId === "custom") return;
    const next = scopesForPreset(presetId);
    if (next) setScopes(next);
  };

  const toggleScope = (id) => {
    setScopes((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      setScopePreset(matchScopePreset(next));
      return next;
    });
  };

  const toggleTeam = (id) => {
    const n = Number(id);
    setTeamIds((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pack) return;

    if (pack.pipe === "hard_inbound") {
      const conversationId = Number(channelId);
      if (!Number.isFinite(conversationId) || conversationId < 1) {
        toastError(null, "Select a channel");
        return;
      }
      if (!scopes.length) {
        toastError(null, "Select at least one scope");
        return;
      }
      if (scopesNeedTeams(scopes) && teamIds.length === 0) {
        toastError(null, "Select at least one team for team/kanban scopes");
        return;
      }
      setSubmitting(true);
      try {
        const body = {
          provider_id: pack.id === "echo-notify" ? "echo" : pack.id,
          pack_id: pack.id,
          conversation_id: conversationId,
          name: name.trim() || pack.default_name || "Circatron",
          scopes,
          team_ids: teamIds,
        };
        if (avatarUrl.trim()) body.avatar_url = avatarUrl.trim();
        const res = await axios.post(
          `${API_BASE_URL}/org/${orgId}/connectors/installations`,
          body,
          { headers: authHeaders }
        );
        toastSuccess("Echo installed — copy secrets now");
        onInstalled?.({
          pack,
          kind: "hard_inbound",
          installation_id: res.data?.installation_id,
          webhook_url: res.data?.webhook_url,
          signing_secret: res.data?.signing_secret,
          bot_token: res.data?.bot_token,
          docs_markdown: pack.docs_markdown,
          success_copy: pack.success_copy,
        });
      } catch (err) {
        toastEntitlementOrError(err, "Failed to install pack", {
          canManageSettings: true,
          orgId,
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (pack.pipe === "incoming") {
      const conversationId = Number(channelId);
      if (!Number.isFinite(conversationId) || conversationId < 1) {
        toastError(null, "Select a channel");
        return;
      }
      setSubmitting(true);
      try {
        const body = {
          conversation_id: conversationId,
          name: name.trim() || pack.default_name || "Incoming webhook",
        };
        if (avatarUrl.trim()) body.avatar_url = avatarUrl.trim();
        const res = await axios.post(
          `${API_BASE_URL}/org/${orgId}/connectors/incoming`,
          body,
          { headers: authHeaders }
        );
        toastSuccess("Pack installed — copy the webhook URL now");
        onInstalled?.({
          pack,
          kind: "incoming",
          url: res.data?.url,
          token: res.data?.token,
          docs_markdown: pack.docs_markdown,
          success_copy: pack.success_copy,
        });
      } catch (err) {
        toastEntitlementOrError(err, "Failed to install pack", {
          canManageSettings: true,
          orgId,
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!targetUrl.trim() || events.length === 0) {
      toastError(null, "URL and at least one event are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/outbound`,
        {
          name: name.trim() || pack.default_name || "Outbound webhook",
          target_url: targetUrl.trim(),
          events,
          enabled: true,
        },
        { headers: authHeaders }
      );
      toastSuccess("Pack installed — copy the signing secret now");
      onInstalled?.({
        pack,
        kind: "outbound",
        signing_secret: res.data?.signing_secret,
        docs_markdown: pack.docs_markdown,
        success_copy: pack.success_copy,
      });
    } catch (err) {
      toastEntitlementOrError(err, "Failed to install pack", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!pack) return null;

  const showTeams =
    fields.includes("scopes") && scopesNeedTeams(scopes);

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-border/80 bg-muted/20 px-4 py-4 space-y-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{pack.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{pack.summary}</p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {fields.includes("channel") ? (
        <div className="space-y-1.5">
          <Label htmlFor="pack-channel">{labels.channel || "Channel"}</Label>
          <select
            id="pack-channel"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            required
          >
            <option value="">Select channel…</option>
            {(channels || []).map((c) => (
              <option key={c.conversation_id} value={String(c.conversation_id)}>
                {c.title || c.team_name || `Channel ${c.conversation_id}`}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {fields.includes("name") ? (
        <div className="space-y-1.5">
          <Label htmlFor="pack-name">{labels.name || "Name"}</Label>
          <Input
            id="pack-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholders.name || ""}
          />
        </div>
      ) : null}

      {fields.includes("avatar_url") ? (
        <div className="space-y-1.5">
          <Label htmlFor="pack-avatar">
            {labels.avatar_url || "Avatar URL (optional)"}
          </Label>
          <Input
            id="pack-avatar"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder={placeholders.avatar_url || "https://…"}
          />
        </div>
      ) : null}

      {fields.includes("scopes") ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>{labels.scopes || "Bot API scopes"}</Label>
            <div className="flex flex-col gap-2">
              {CIRCATRON_SCOPE_PRESETS.map((p) => (
                <label
                  key={p.id}
                  className="flex items-start gap-2 text-sm text-foreground"
                >
                  <input
                    type="radio"
                    name="scope-preset"
                    className="mt-1"
                    checked={scopePreset === p.id}
                    onChange={() => applyPreset(p.id)}
                  />
                  <span>
                    <span className="font-medium">{p.label}</span>
                    <span className="block text-xs text-muted-foreground">
                      {p.description}
                    </span>
                  </span>
                </label>
              ))}
              <label className="flex items-start gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="scope-preset"
                  className="mt-1"
                  checked={scopePreset === "custom"}
                  onChange={() => applyPreset("custom")}
                />
                <span>
                  <span className="font-medium">Custom</span>
                  <span className="block text-xs text-muted-foreground">
                    Pick individual scopes below
                  </span>
                </span>
              </label>
            </div>
          </div>
          <div className="flex flex-col gap-2 pl-1 border-l border-border/60">
            {SCOPE_OPTIONS.map((sc) => (
              <label
                key={sc.id}
                className="flex items-center gap-2 text-sm text-foreground"
              >
                <input
                  type="checkbox"
                  checked={scopes.includes(sc.id)}
                  onChange={() => toggleScope(sc.id)}
                />
                {sc.label}
                <span className="text-xs text-muted-foreground">({sc.id})</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {showTeams ? (
        <div className="space-y-2">
          <Label>Allowed teams</Label>
          {(teams || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No teams available.</p>
          ) : (
            <div className="flex flex-col gap-2 max-h-40 overflow-y-auto">
              {(teams || []).map((t) => {
                const id = Number(t.team_id ?? t.teamId);
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2 text-sm text-foreground"
                  >
                    <input
                      type="checkbox"
                      checked={teamIds.includes(id)}
                      onChange={() => toggleTeam(id)}
                    />
                    {t.name || `Team ${id}`}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {fields.includes("target_url") ? (
        <div className="space-y-1.5">
          <Label htmlFor="pack-url">{labels.target_url || "Target URL"}</Label>
          <Input
            id="pack-url"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder={placeholders.target_url || "https://…"}
            required
          />
        </div>
      ) : null}

      {fields.includes("events") ? (
        <div className="space-y-2">
          <Label>{labels.events || "Events"}</Label>
          <div className="flex flex-wrap gap-3">
            {EVENT_OPTIONS.map((ev) => (
              <label
                key={ev.id}
                className="flex items-center gap-2 text-sm text-foreground"
              >
                <input
                  type="checkbox"
                  checked={events.includes(ev.id)}
                  onChange={() => toggleEvent(ev.id)}
                />
                {ev.label}
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 pt-1">
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? "Installing…" : "Install"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CircatronPackInstallWizard;
